import { normalizeRut } from './rut';

export const PROVIDER_TEXT_MAX_LENGTH = 255;
export const PROVIDER_PAYMENT_MAX_AMOUNT = 2147483647;
export const PROVIDER_PAYMENT_METHODS = ['efectivo', 'transferencia', 'cheque', 'tarjeta'];
export const PROVIDER_PAYMENT_METHOD_LABELS = {
  efectivo: 'Efectivo',
  transferencia: 'Transferencia',
  cheque: 'Cheque',
  tarjeta: 'Tarjeta',
};

export const EMPTY_PROVIDER = {
  name: '',
  rut: '',
  giro: '',
  email: '',
  phone: '',
  address: '',
  city: '',
};

export const normalizeValue = (value) => (value || '').trim();

export const createEmptyAccount = () => ({
  id: null,
  bankName: '',
  bankAccount: '',
  bankAccountType: '',
  bankRut: '',
});

export const buildAccountPayload = (account) => ({
  bankName: normalizeValue(account.bankName),
  bankAccount: normalizeValue(account.bankAccount),
  bankAccountType: normalizeValue(account.bankAccountType),
  bankRut: normalizeValue(account.bankRut),
});

const buildTextLimitMessage = (label) =>
  `${label} no puede superar ${PROVIDER_TEXT_MAX_LENGTH} caracteres.`;

const getExceededFieldMessage = (value, label) =>
  normalizeValue(value).length > PROVIDER_TEXT_MAX_LENGTH
    ? buildTextLimitMessage(label)
    : '';

const getNormalizedProviderName = (value) => normalizeValue(value).toLowerCase();

const buildProviderPayload = (form = {}) => ({
  name: normalizeValue(form.name),
  rut: normalizeValue(form.rut),
  giro: normalizeValue(form.giro),
  email: normalizeValue(form.email),
  phone: normalizeValue(form.phone),
  address: normalizeValue(form.address),
  city: normalizeValue(form.city),
});

export const validateProviderTextFields = (form = {}) => {
  const fields = [
    ['name', 'El nombre'],
    ['rut', 'El RUT'],
    ['giro', 'El giro'],
    ['email', 'El email'],
    ['phone', 'El telefono'],
    ['address', 'La direccion'],
    ['city', 'La ciudad'],
  ];

  for (const [key, label] of fields) {
    const error = getExceededFieldMessage(form[key], label);
    if (error) {
      return error;
    }
  }

  return '';
};

export const findProviderDuplicateError = (providers = [], form = {}, currentProviderId = null) => {
  const normalizedName = getNormalizedProviderName(form.name);
  const normalizedRutValue = normalizeRut(form.rut);
  const currentProviderKey = currentProviderId == null ? null : String(currentProviderId);

  if (normalizedName) {
    const duplicateName = providers.some((provider) => {
      if (currentProviderKey !== null && String(provider.id) === currentProviderKey) {
        return false;
      }
      return getNormalizedProviderName(provider.name) === normalizedName;
    });
    if (duplicateName) {
      return 'Ya existe un proveedor con ese nombre.';
    }
  }

  if (normalizedRutValue) {
    const duplicateRut = providers.some((provider) => {
      if (currentProviderKey !== null && String(provider.id) === currentProviderKey) {
        return false;
      }
      return normalizeRut(provider.rut) === normalizedRutValue;
    });
    if (duplicateRut) {
      return 'Ya existe un proveedor con ese RUT.';
    }
  }

  return '';
};

export const validateProviderAccounts = (accounts = [], options = {}) => {
  const { rejectPersistedEmptyAccount = false } = options;
  const payloads = [];

  for (let i = 0; i < accounts.length; i++) {
    const account = accounts[i];
    const payload = buildAccountPayload(account);
    const hasAny = Object.values(payload).some((value) => value);

    if (!hasAny) {
      if (rejectPersistedEmptyAccount && account.id) {
        return { error: `Cuenta ${i + 1} sin datos. Eliminala si no se usa.` };
      }
      continue;
    }

    if (!payload.bankName || !payload.bankAccount) {
      return { error: `Completa banco y numero de cuenta en la cuenta ${i + 1}.` };
    }

    const fieldErrors = [
      getExceededFieldMessage(payload.bankName, 'El banco'),
      getExceededFieldMessage(payload.bankAccount, 'El numero de cuenta'),
      getExceededFieldMessage(payload.bankAccountType, 'El tipo de cuenta'),
      getExceededFieldMessage(payload.bankRut, 'El RUT titular'),
    ].filter(Boolean);

    if (fieldErrors.length > 0) {
      return { error: `${fieldErrors[0]} (cuenta ${i + 1})` };
    }

    payloads.push({ ...payload, id: account.id || null });
  }

  return { payloads };
};

export const validateProviderForm = ({
  form = {},
  accounts = [],
  providers = [],
  currentProviderId = null,
  rejectPersistedEmptyAccount = false,
} = {}) => {
  const payload = buildProviderPayload(form);

  if (!payload.name) {
    return { error: 'El nombre del proveedor es obligatorio.' };
  }

  const textError = validateProviderTextFields(payload);
  if (textError) {
    return { error: textError };
  }

  const duplicateError = findProviderDuplicateError(providers, payload, currentProviderId);
  if (duplicateError) {
    return { error: duplicateError };
  }

  const { payloads: accountPayloads, error: accountsError } = validateProviderAccounts(accounts, {
    rejectPersistedEmptyAccount,
  });
  if (accountsError) {
    return { error: accountsError };
  }

  return { payload, accountPayloads };
};

export const validateProviderPaymentForm = (form = {}) => {
  const amount = Number(form.amount);

  if (!Number.isFinite(amount) || amount <= 0) {
    return { error: 'Ingresa un monto valido.' };
  }

  if (!Number.isInteger(amount)) {
    return { error: 'El monto debe ser un numero entero.' };
  }

  if (amount > PROVIDER_PAYMENT_MAX_AMOUNT) {
    return {
      error: `El monto no puede superar $${PROVIDER_PAYMENT_MAX_AMOUNT.toLocaleString('de-DE')}.`,
    };
  }

  if (!PROVIDER_PAYMENT_METHODS.includes(form.method)) {
    return { error: 'Selecciona un metodo de pago valido.' };
  }

  if (form.paidAt) {
    const paidAt = new Date(form.paidAt);
    if (Number.isNaN(paidAt.getTime())) {
      return { error: 'La fecha de pago no es valida.' };
    }
  }

  const referenceError = getExceededFieldMessage(form.reference, 'La referencia');
  if (referenceError) {
    return { error: referenceError };
  }

  const noteError = getExceededFieldMessage(form.note, 'La nota');
  if (noteError) {
    return { error: noteError };
  }

  return { amount };
};

export const getProviderPaymentMethodLabel = (method) =>
  PROVIDER_PAYMENT_METHOD_LABELS[method] || method || '—';

export const getProviderApiErrorMessage = (error, fallbackMessage) =>
  error?.response?.data?.message || error?.response?.data?.error || fallbackMessage;
